import {
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  watch,
  type InjectionKey,
  type Ref,
} from 'vue';
import { useNxCommands, type NxCommand } from '../CommandPalette/commands';

/**
 * „Was möchten Sie tun?“ for the office ribbon (I-2348): a host that provides
 * these options makes every ribbon button searchable in the ⌘K palette while
 * the ribbon is mounted. The commands are read from the rendered ribbon —
 * nobody lists them by hand — and picking one presses the button, so the
 * palette runs exactly what the ribbon runs.
 */
export interface NxOfficeRibbonCommandOptions {
  /** The app key: command ids start with it, and the open app ranks them first. */
  app: string;
  /** Group heading in the palette (usually the app's name). */
  group: string | (() => string);
}

export const nxOfficeRibbonCommandsKey: InjectionKey<NxOfficeRibbonCommandOptions> =
  Symbol('nxOfficeRibbonCommands');

/** Call in the host's `setup()` above the ribbon to put its buttons into the palette. */
export function provideNxOfficeRibbonCommands(options: NxOfficeRibbonCommandOptions): void {
  provide(nxOfficeRibbonCommandsKey, options);
}

/** One button of the ribbon. */
export interface NxRibbonItem {
  /** Stable within one ribbon: tab plus label. */
  key: string;
  /** The tab it sits on; `null` for the document bar and quick access. */
  tab: string | null;
  label: string;
  element: HTMLElement;
}

/**
 * Marks the body of one tab (`data-nx-ribbon-tab="home"`). Needed where a host
 * keeps every tab body mounted (Calc); a body without it belongs to the
 * active tab. `data-nx-ribbon-skip` keeps a subtree out of the palette.
 */
export const RIBBON_TAB_ATTRIBUTE = 'data-nx-ribbon-tab';

export function ribbonItemLabel(element: HTMLElement): string {
  const raw =
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.textContent ||
    '';
  return raw.replace(/\s+/g, ' ').trim();
}

function isDisabled(element: HTMLElement): boolean {
  return (
    (element as HTMLButtonElement).disabled === true ||
    element.getAttribute('aria-disabled') === 'true'
  );
}

/**
 * The buttons of a rendered ribbon, without the tab strip, open menus and
 * lists. A label repeated on one tab (the arrow half of a split button with
 * the same name) is kept once.
 */
export function collectRibbonItems(root: HTMLElement, activeTab: string): NxRibbonItem[] {
  const out: NxRibbonItem[] = [];
  const seen = new Set<string>();
  for (const element of root.querySelectorAll<HTMLElement>('button, [role="button"]')) {
    if (element.closest('[role="tablist"], [role="menu"], [role="listbox"], [data-nx-ribbon-skip]'))
      continue;
    const label = ribbonItemLabel(element);
    if (!label) continue;
    const body = element.closest<HTMLElement>(`[${RIBBON_TAB_ATTRIBUTE}]`);
    const tab = body
      ? (body.getAttribute(RIBBON_TAB_ATTRIBUTE) ?? activeTab)
      : element.closest('.nx-office-ribbon__documentbar, .nx-office-ribbon__quick-access')
        ? null
        : activeTab;
    const key = `${tab ?? ''}::${label.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, tab, label, element });
  }
  return out;
}

function slug(text: string): string {
  const s = text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'item';
}

export interface NxRibbonTabLike {
  readonly id: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface UseNxOfficeRibbonCommandsOptions {
  root: Ref<HTMLElement | null>;
  activeTab: () => string;
  tabs: () => readonly NxRibbonTabLike[];
  /** Switches the ribbon to a tab (the ribbon's `select`). */
  select: (tab: string) => void;
  /** Caption of a tab's command list, e.g. „Einfügen: Befehle“. */
  tabCommandsLabel: (tab: string) => string;
}

/**
 * Registers the ribbon's buttons as palette commands while the ribbon is
 * mounted, if the host asked for it (`provideNxOfficeRibbonCommands`). A host
 * that renders only the active tab (Write) grows the list as tabs are visited;
 * each tab also gets an entry that opens it and lists its buttons.
 */
export function useNxOfficeRibbonCommands(options: UseNxOfficeRibbonCommandsOptions): void {
  const host = inject(nxOfficeRibbonCommandsKey, null);
  const registry = useNxCommands();
  if (!host || !registry) return;
  const app = host.app;
  const group = () => (typeof host.group === 'function' ? host.group() : host.group);

  const catalog = new Map<string, NxRibbonItem>();
  const ids = new Map<string, string>();
  let unregister: () => void = () => {};
  let signature = '';

  function scan(): void {
    const root = options.root.value;
    if (!root) return;
    const active = options.activeTab();
    const current = collectRibbonItems(root, active);
    const rendered = new Set<string | null>([null, active]);
    for (const body of root.querySelectorAll(`[${RIBBON_TAB_ATTRIBUTE}]`)) {
      rendered.add(body.getAttribute(RIBBON_TAB_ATTRIBUTE));
    }
    const keys = new Set(current.map((item) => item.key));
    for (const [key, item] of catalog) {
      if (rendered.has(item.tab) && !keys.has(key)) catalog.delete(key);
    }
    for (const item of current) catalog.set(item.key, item);
  }

  function idOf(item: NxRibbonItem): string {
    let id = ids.get(item.key);
    if (id) return id;
    const base = `${app}.ribbon.${slug(item.tab ?? 'document')}.${slug(item.label)}`;
    const taken = new Set(ids.values());
    id = base;
    for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
    ids.set(item.key, id);
    return id;
  }

  function tabOf(id: string | null): NxRibbonTabLike | undefined {
    return id ? options.tabs().find((tab) => tab.id === id) : undefined;
  }

  function available(key: string): boolean {
    const item = catalog.get(key);
    if (!item) return false;
    const root = options.root.value;
    if (item.element.isConnected && root?.contains(item.element)) return !isDisabled(item.element);
    if (item.tab === null) return false;
    const tab = tabOf(item.tab);
    return !!tab && !tab.disabled;
  }

  async function showTab(tab: string): Promise<void> {
    if (options.activeTab() === tab) return;
    options.select(tab);
    await nextTick();
    if (options.activeTab() !== tab) await nextTick();
    scan();
  }

  async function press(key: string): Promise<void> {
    const item = catalog.get(key);
    if (!item) return;
    if (item.tab !== null) await showTab(item.tab);
    const element = catalog.get(key)?.element;
    if (!element?.isConnected || isDisabled(element)) return;
    element.click();
  }

  function toCommand(item: NxRibbonItem): NxCommand {
    const tabLabel = tabOf(item.tab)?.label;
    return {
      id: idOf(item),
      label: item.label,
      hint: tabLabel,
      group: group(),
      app,
      category: 'action',
      keywords: tabLabel ? [tabLabel] : undefined,
      when: () => available(item.key),
      run: () => press(item.key),
    };
  }

  function commandsOfTab(tab: string): NxCommand[] {
    return [...catalog.values()]
      .filter((item) => item.tab === tab && available(item.key))
      .map(toCommand);
  }

  function build(): NxCommand[] {
    const items = [...catalog.values()].map(toCommand);
    const tabs = options
      .tabs()
      .filter((tab) => !tab.disabled)
      .map(
        (tab): NxCommand => ({
          id: `${app}.ribbon.tab.${slug(tab.id)}`,
          label: options.tabCommandsLabel(tab.label),
          group: group(),
          app,
          category: 'navigation',
          keywords: [tab.label],
          when: () => !!tabOf(tab.id) && !tabOf(tab.id)?.disabled,
          run: () => showTab(tab.id),
          children: async () => {
            await showTab(tab.id);
            return commandsOfTab(tab.id);
          },
        })
      );
    return [...items, ...tabs];
  }

  function refresh(): void {
    scan();
    const commands = build();
    const next = commands
      .map((c) => `${c.id}\u0000${c.label}\u0000${c.hint ?? ''}\u0000${c.group ?? ''}`)
      .join('\n');
    if (next === signature) return;
    signature = next;
    unregister();
    unregister = registry!.register(commands);
  }

  onMounted(() => void nextTick(refresh));
  watch([options.activeTab, options.tabs], () => void nextTick(refresh), { flush: 'post' });
  // The palette opening is the moment its list must be current (labels,
  // contextual tabs, a group label in a new language).
  watch(
    () => registry.isOpen.value,
    (open) => {
      if (open) refresh();
    }
  );
  onBeforeUnmount(() => {
    unregister();
    unregister = () => {};
  });
}
